<template>
  <div class="card border-0 rounded-3 mb-4 bg-white shadow-sm">
    <div class="card-header bg-light-subtle d-flex justify-content-between align-items-center py-3 px-4">
      <div class="d-flex align-items-center gap-2">
        <span class="material-symbols-outlined text-primary fs-22">fact_check</span>
        <h5 class="mb-0 fs-15 fw-bold text-dark">Protokol Pemeriksaan Similaritas (SOP RBN-RC-SIM-01)</h5>
      </div>
      <span v-if="protocol" class="badge bg-success-subtle text-success fs-12 px-3 py-2">
        {{ protocol.protocol_number }}
      </span>
      <span v-else class="badge bg-warning-subtle text-warning fs-12 px-3 py-2">
        Belum Dibuat
      </span>
    </div>

    <div class="card-body p-4">
      <!-- Existing Protocol Overview (Template Output Otomatis RBN-RC-WEB-SIM-01) -->
      <div v-if="protocol" class="mb-4 p-3 rounded-3 bg-light border border-light-subtle">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <div class="text-muted fs-12">Nomor Protokol Resmi (SOP RBN-RC-SIM-01)</div>
            <h5 class="fw-bold text-primary mb-0">{{ protocol.protocol_number }}</h5>
          </div>
          <div class="d-flex gap-2">
            <button @click="generatePdf" :disabled="generatingPdf" class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1">
              <span v-if="generatingPdf" class="spinner-border spinner-border-sm"></span>
              <span class="material-symbols-outlined fs-16">picture_as_pdf</span>
              <span>{{ generatingPdf ? 'Membuat PDF...' : 'Regenerate PDF Protokol' }}</span>
            </button>
            <a v-if="order.generated_protocol_pdf" :href="getFileUrl(order.generated_protocol_pdf)" target="_blank" class="btn btn-sm btn-success d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16">download</span>
              <span>Unduh Dokumen Resmi A4</span>
            </a>
            <a v-if="protocol.verification?.verification_code" :href="getVerificationUrl(protocol.verification.verification_code)" target="_blank" class="btn btn-sm btn-info text-white d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16">verified</span>
              <span>Cek Verifikasi Publik</span>
            </a>
          </div>
        </div>

        <div class="row g-2 fs-13">
          <div class="col-md-3">
            <span class="text-muted">Skor Similaritas:</span>
            <span class="fw-bold text-dark ms-1">{{ protocol.overall_before }}% &rarr; {{ protocol.overall_after }}%</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Sumber Tertinggi:</span>
            <span class="fw-bold text-dark ms-1">{{ protocol.highest_source_match }}%</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Status:</span>
            <span class="badge ms-1" :class="protocol.result_status === 'Memenuhi' ? 'bg-success' : 'bg-danger'">{{ protocol.result_status }}</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Rekomendasi:</span>
            <span class="fw-semibold text-dark ms-1">{{ protocol.recommendation }}</span>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2 pt-2 border-top fs-12 text-muted">
          <div class="d-flex gap-3">
            <div>
              <span class="material-symbols-outlined fs-14 align-middle text-primary">format_list_numbered</span>
              <strong class="text-dark ms-1">{{ (protocol.primary_sources || protocol.primarySources || []).length }}</strong> Primary Sources
            </div>
            <div>
              <span class="material-symbols-outlined fs-14 align-middle text-warning">rule</span>
              <strong class="text-dark ms-1">{{ (protocol.manual_exclusions || protocol.manualExclusions || []).length }}</strong> Manual Source Exclusion
            </div>
          </div>
          <button type="button" class="btn btn-xs btn-outline-secondary fs-11 py-1 px-2 d-flex align-items-center gap-1" @click="showDetailedOverview = !showDetailedOverview">
            <span class="material-symbols-outlined fs-14">{{ showDetailedOverview ? 'expand_less' : 'expand_more' }}</span>
            <span>{{ showDetailedOverview ? 'Tutup Rincian Tabel SOP' : 'Lihat Rincian Template Output (SOP)' }}</span>
          </button>
        </div>

        <!-- Detail Preview Tabel Output Template Resmi -->
        <div v-if="showDetailedOverview" class="mt-3 pt-3 border-top">
          <!-- Pengaturan Pemeriksaan -->
          <div class="p-2 mb-3 bg-white rounded border">
            <div class="fw-bold fs-12 text-secondary mb-2">Pengaturan Pemeriksaan (SOP RBN-RC-SIM-01):</div>
            <div class="row g-2 fs-12 text-dark">
              <div class="col-md-3">
                <span class="text-muted">Repository:</span> <strong>{{ protocol.repository_setting || 'No repository' }}</strong>
              </div>
              <div class="col-md-3">
                <span class="text-muted">Exclude Bibliography:</span> <strong>{{ protocol.applied_exclude_biblio ? 'Excluded' : 'Included' }}</strong>
              </div>
              <div class="col-md-3">
                <span class="text-muted">Exclude Quoted Material:</span> <strong>{{ protocol.applied_exclude_quotes ? 'Excluded' : 'Included' }}</strong>
              </div>
              <div class="col-md-3">
                <span class="text-muted">Exclude Small Matches:</span> <strong>{{ formatSmallMatches(protocol) }}</strong>
              </div>
              <div class="col-12 pt-1 border-top mt-1">
                <span class="text-muted">Manual Exclusion:</span> 
                <span class="badge bg-warning-subtle text-dark ms-1">{{ (protocol.manual_exclusions || protocol.manualExclusions || []).length }} sumber</span>
              </div>
            </div>
          </div>

          <!-- Primary Source Utama Preview Table -->
          <div class="mb-3">
            <div class="fw-bold fs-12 text-dark mb-1 d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-14 text-primary">format_list_numbered</span>
              <span>Primary Source Utama</span>
              <span class="badge bg-primary-subtle text-primary fs-10 ms-1">
                {{ (protocol.primary_sources || protocol.primarySources || []).length }} Sumber
              </span>
            </div>
            <div class="table-responsive">
              <table class="table table-sm table-bordered bg-white align-middle mb-0 fs-12">
                <thead class="table-light">
                  <tr>
                    <th style="width: 5%;" class="text-center">No.</th>
                    <th style="width: 45%;">Sumber</th>
                    <th style="width: 12%;" class="text-center">Match</th>
                    <th style="width: 15%;" class="text-center">Status Review</th>
                    <th style="width: 23%;">Catatan</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(src, idx) in (protocol.primary_sources || protocol.primarySources || [])" :key="idx">
                    <td class="text-center">{{ src.source_rank || (idx + 1) }}</td>
                    <td>{{ src.source_name }}</td>
                    <td class="text-center fw-bold">{{ src.match_percentage }}%</td>
                    <td class="text-center">
                      <span class="badge" :class="src.status === 'Wajar' ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning-emphasis'">
                        {{ src.status || 'Wajar' }}
                      </span>
                    </td>
                    <td>{{ src.examiner_notes || '-' }}</td>
                  </tr>
                  <tr v-if="!(protocol.primary_sources || protocol.primarySources || []).length">
                    <td colspan="5" class="text-center text-muted py-2">Belum ada data sumber primer tersimpan.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 2. BLOK MANUAL EXCLUSION (Sesuai Halaman 3 PDF) -->
          <div>
            <div class="fw-bold fs-12 text-dark mb-1 d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-14 text-warning">rule</span>
              <span>Manual Source Exclusion</span>
              <span class="badge bg-warning-subtle text-dark fs-10 ms-1">
                {{ (protocol.manual_exclusions || protocol.manualExclusions || []).length }} Sumber
              </span>
            </div>
            <div class="table-responsive">
              <table class="table table-sm table-bordered bg-white align-middle mb-0 fs-12">
                <thead class="table-light">
                  <tr>
                    <th style="width: 32%;">Sumber</th>
                    <th style="width: 16%;" class="text-center">Match Sebelum Exclude</th>
                    <th style="width: 26%;">Alasan</th>
                    <th style="width: 12%;" class="text-center">Petugas</th>
                    <th style="width: 14%;" class="text-center">Waktu</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(exc, idx) in (protocol.manual_exclusions || protocol.manualExclusions || [])" :key="idx">
                    <td>{{ exc.source_title_url }}</td>
                    <td class="text-center fw-bold text-danger">{{ exc.match_before }}%</td>
                    <td>{{ exc.exclusion_reason }}</td>
                    <td class="text-center">{{ exc.authorized_by }}</td>
                    <td class="text-center text-muted fs-11">{{ exc.excluded_at || '-' }}</td>
                  </tr>
                  <tr v-if="!(protocol.manual_exclusions || protocol.manualExclusions || []).length">
                    <td colspan="5" class="text-center text-muted py-2">
                      Tidak ada manual exclusion (Pemeriksaan mengikuti pengaturan filter standar).
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Creation / Edit Form -->
      <form @submit.prevent="saveProtocol">

        <!-- 2. PEMERIKSAAN IDENTITAS DOKUMEN & SOFTWARE (SOP RBN-RC-SIM-01) -->
        <div class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex align-items-center gap-2 py-2 px-3">
            <span class="material-symbols-outlined text-primary fs-18">badge</span>
            <span class="fw-bold fs-13 text-dark">2. Pemeriksaan Identitas & Parameter Pengujian (SOP RBN-RC-SIM-01)</span>
          </div>
          <div class="card-body p-3">
            <!-- Preset & Classification Mapping -->
            <div class="row mb-3">
              <div class="col-md-6 mb-2">
                <label class="form-label fs-13 fw-semibold">Preset Ambang Batas (Similarity Profile)</label>
                <select class="form-select form-select-sm" v-model="form.profile_id" @change="onProfileChange">
                  <option :value="null">-- Custom / Bebas --</option>
                  <option v-for="p in profiles" :key="p.id" :value="p.id">
                    {{ p.service_classification }} (Max {{ p.default_max_overall }}%, Single {{ p.default_max_single_source }}%)
                  </option>
                </select>
              </div>
              <div class="col-md-6 mb-2">
                <label class="form-label fs-13 fw-semibold">Klasifikasi Dokumen <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.service_classification" required>
                  <option value="Artikel Jurnal">Artikel Jurnal</option>
                  <option value="JAD Dosen">JAD Dosen</option>
                  <option value="Skripsi/Tesis/Disertasi">Skripsi/Tesis/Disertasi</option>
                  <option value="Proposal">Proposal</option>
                  <option value="Laporan">Laporan</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
            </div>

            <!-- Tool, Repository & Manuscript Version -->
            <div class="row mb-3">
              <div class="col-md-4 mb-2">
                <label class="form-label fs-13 fw-semibold">Perangkat Lunak Penguji</label>
                <input type="text" class="form-control form-control-sm" v-model="form.tool_name" required placeholder="Turnitin / iThenticate" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-13 fw-semibold">Setting Repositori <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.repository_setting" required>
                  <option value="No repository">No repository (Wajib SOP RBN)</option>
                  <option value="Standard repository">Standard repository</option>
                  <option value="Sesuai kebijakan akun">Sesuai kebijakan akun</option>
                </select>
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-13 fw-semibold">Versi Naskah</label>
                <select class="form-select form-select-sm" v-model="form.manuscript_version">
                  <option value="Draft">Draft</option>
                  <option value="Accepted manuscript">Accepted manuscript</option>
                  <option value="Published version">Published version</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
            </div>

            <!-- Submission ID, Examiner Account & Report Date -->
            <div class="row mb-3">
              <div class="col-md-4 mb-2">
                <label class="form-label fs-13 fw-semibold">ID Laporan / Submission ID</label>
                <input type="text" class="form-control form-control-sm" v-model="form.report_submission_id" placeholder="cth: 219830219" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-13 fw-semibold">Akun Pemeriksa Turnitin</label>
                <input type="text" class="form-control form-control-sm" v-model="form.examiner_account" placeholder="cth: Turnitin PT RBN Official" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-13 fw-semibold">Tanggal Laporan Software</label>
                <input type="date" class="form-control form-control-sm" v-model="form.report_date" />
              </div>
            </div>

            <!-- SHA256 Hash -->
            <div class="mb-0">
              <label class="form-label fs-13 fw-semibold">SHA-256 Hash Naskah</label>
              <input type="text" class="form-control form-control-sm font-monospace" v-model="form.file_hash_sha256" required />
            </div>
          </div>
        </div>

        <!-- 3. PENGATURAN PEMERIKSAAN & PARAMETER AMBANG BATAS (SNAPSHOT OVERRIDE) -->
        <div class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex justify-content-between align-items-center py-2 px-3">
            <div class="d-flex align-items-center gap-2">
              <span class="material-symbols-outlined text-primary fs-18">tune</span>
              <span class="fw-bold fs-13 text-dark">3. Pengaturan Pemeriksaan & Parameter Ambang Batas (Snapshot Override)</span>
            </div>
            <span class="badge bg-warning-subtle text-dark fs-11">
              Manual Exclusion: {{ form.manual_exclusions.length }} sumber
            </span>
          </div>
          <div class="card-body p-3">
            <!-- Sync Info dari Permintaan Pelanggan saat Checkout -->
            <div v-if="orderRequirement" class="mb-3 p-2 bg-white rounded border d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="d-flex align-items-center flex-wrap gap-2 fs-12">
                <span class="badge bg-primary-subtle text-primary fs-11">Pilihan Pelanggan pada Pesanan</span>
                <span class="text-secondary">
                  Exclude Quotes: <strong class="text-dark">{{ orderRequirement.excludeQuotes ? 'Ya' : 'Tidak' }}</strong> &bull;
                  Exclude Biblio: <strong class="text-dark">{{ orderRequirement.excludeBiblio ? 'Ya' : 'Tidak' }}</strong> &bull;
                  Exclude Match: <strong :class="orderRequirement.excludeMatch ? 'text-success' : 'text-secondary'">{{ orderRequirement.excludeMatch ? 'Ya (Aktif)' : 'Tidak (None)' }}</strong>
                </span>
              </div>
              <button type="button" class="btn btn-xs btn-outline-primary py-0 px-2 fs-11 d-inline-flex align-items-center gap-1" @click="syncFromOrderRequirements(true)">
                <span class="material-symbols-outlined fs-13">sync</span>
                <span>Sinkronkan ke Form</span>
              </button>
            </div>

            <div class="row">
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12">Maks. Total (%)</label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.applied_max_overall" @input="onThresholdChange" required />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12">Maks. Sumber Tunggal (%)</label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.applied_max_single_source" @input="onThresholdChange" required />
              </div>
              <div class="col-md-3 mb-2 d-flex align-items-center pt-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="biblioCheck" v-model="form.applied_exclude_biblio">
                  <label class="form-check-label fs-12" for="biblioCheck">Exclude Bibliografi</label>
                </div>
              </div>
              <div class="col-md-3 mb-2 d-flex align-items-center pt-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="quotesCheck" v-model="form.applied_exclude_quotes">
                  <label class="form-check-label fs-12" for="quotesCheck">Exclude Quotes</label>
                </div>
              </div>
            </div>
            <div class="row mt-2 pt-2 border-top g-2 align-items-center">
              <div class="col-md-6">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <label class="form-label fs-12 mb-0">Exclude Small Matches (Filter Kecocokan Kecil)</label>
                  <span v-if="orderRequirement?.excludeMatch" class="badge bg-success-subtle text-success fs-10">Pilihan Pelanggan: Aktif</span>
                </div>
                <div class="input-group input-group-sm">
                  <select class="form-select form-select-sm" v-model="form.small_matches_mode">
                    <option value="none">Tidak / None</option>
                    <option value="words">Words (Jumlah Kata)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                  <input v-if="form.small_matches_mode !== 'none'" type="number" min="1" class="form-control form-control-sm" v-model.number="form.small_matches_value" :placeholder="form.small_matches_mode === 'words' ? 'cth: 8' : 'cth: 1'" />
                  <span v-if="form.small_matches_mode !== 'none'" class="input-group-text fs-12">{{ form.small_matches_mode === 'words' ? 'kata' : '%' }}</span>
                </div>
              </div>
              <div class="col-md-6 d-flex justify-content-between align-items-center pt-3">
                <span class="fs-12 text-secondary">
                  Manual Source Exclusion: <strong class="text-dark">{{ form.manual_exclusions.length }} sumber dicatat</strong>
                </span>
                <a href="#manualExclusionCard" class="btn btn-xs btn-outline-warning text-dark fs-12 py-1 px-2 d-inline-flex align-items-center gap-1">
                  <span class="material-symbols-outlined fs-14">rule</span>
                  <span>Kelola Manual Source Exclusion</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. MANUAL SOURCE EXCLUSION (SOP RBN-RC-SIM-01) -->
        <div id="manualExclusionCard" class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex justify-content-between align-items-center py-2 px-3">
            <div class="d-flex align-items-center gap-2">
              <span class="material-symbols-outlined text-warning fs-18">rule</span>
              <div>
                <span class="fw-bold fs-13 text-dark">4. Manual Source Exclusion (Log Eksklusi Manual Sumber) - SOP RBN-RC-SIM-01</span>
                <span class="badge bg-warning-subtle text-dark fs-11 ms-2">{{ form.manual_exclusions.length }} Sumber Dieksklusi</span>
              </div>
            </div>
            <button type="button" @click="addManualExclusion" class="btn btn-xs btn-warning text-dark d-flex align-items-center gap-1 py-1 px-2 fs-12 fw-medium shadow-sm">
              <span class="material-symbols-outlined fs-14">add</span>
              <span>Tambah Manual Source Exclusion</span>
            </button>
          </div>
          <div class="card-body p-3">
            <p class="fs-12 text-muted mb-2">
              <span class="material-symbols-outlined fs-14 align-middle text-primary">info</span>
              <strong>Blok 2 Template Output Resmi (RBN-RC-WEB-SIM-01)</strong>: Sesuai SOP, jika pemeriksa mengecualikan kecocokan tertentu (seperti publikasi/preprint milik penulis sendiri, repository institusi, atau template resmi), cantumkan rincian dan alasannya demi audit kepatuhan integritas akademik.
            </p>
            <div v-if="form.manual_exclusions.length === 0" class="text-center py-3 text-muted fs-12 bg-light-subtle rounded border border-dashed">
              <span class="material-symbols-outlined fs-24 d-block mb-1 text-secondary">verified_user</span>
              Tidak ada sumber yang dieksklusi secara manual (Pemeriksaan mengikuti pengaturan filter standar Turnitin).
              <div class="mt-2">
                <button type="button" @click="addManualExclusion" class="btn btn-xs btn-outline-warning text-dark fs-12 py-1 px-2">
                  <span class="material-symbols-outlined fs-14 align-middle">add</span>
                  Tambah Manual Source Exclusion
                </button>
              </div>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered align-middle mb-0 fs-12">
                <thead class="table-light">
                  <tr>
                    <th style="width: 27%;">Sumber / Dokumen / URL <span class="text-danger">*</span></th>
                    <th style="width: 14%;">Tipe Sumber</th>
                    <th style="width: 12%;" class="text-center">Match Sblm Exclude (%) <span class="text-danger">*</span></th>
                    <th style="width: 21%;">Alasan Justifikasi Akademik <span class="text-danger">*</span></th>
                    <th style="width: 11%;">Petugas</th>
                    <th style="width: 11%;">Waktu Exclude</th>
                    <th style="width: 4%;" class="text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(exc, idx) in form.manual_exclusions" :key="idx">
                    <td>
                      <input type="text" class="form-control form-control-sm" v-model="exc.source_title_url" placeholder="cth: Preprint Penulis di ResearchGate" required />
                    </td>
                    <td>
                      <select class="form-select form-select-sm" v-model="exc.source_type">
                        <option value="Self-match">Self-match</option>
                        <option value="Bibliografi">Bibliografi</option>
                        <option value="Quote">Quote</option>
                        <option value="Lainnya">Lainnya</option>
                      </select>
                    </td>
                    <td>
                      <div class="input-group input-group-sm">
                        <input type="number" step="0.1" class="form-control form-control-sm text-center" v-model.number="exc.match_before" required />
                        <span class="input-group-text px-1">%</span>
                      </div>
                    </td>
                    <td>
                      <input type="text" class="form-control form-control-sm" v-model="exc.exclusion_reason" placeholder="cth: Publikasi skripsi penulis sendiri yang sudah terbit" required />
                    </td>
                    <td>
                      <select class="form-select form-select-sm" v-model="exc.authorized_by">
                        <option value="Pemeriksa">Pemeriksa</option>
                        <option value="QC">QC</option>
                      </select>
                    </td>
                    <td>
                      <input type="text" class="form-control form-control-sm text-center fs-11" v-model="exc.excluded_at" placeholder="YYYY-MM-DD HH:mm:ss" />
                    </td>
                    <td class="text-center">
                      <button type="button" @click="removeManualExclusion(idx)" class="btn btn-sm btn-outline-danger p-1 border-0" title="Hapus Manual Exclusion">
                        <span class="material-symbols-outlined fs-16">delete</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- 5. DAFTAR SUMBER PRIMER (PRIMARY SOURCES) -->
        <div class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex justify-content-between align-items-center py-2 px-3">
            <div class="d-flex align-items-center gap-2">
              <span class="material-symbols-outlined text-primary fs-18">format_list_numbered</span>
              <span class="fw-bold fs-13 text-dark">5. Daftar Sumber Kecocokan Utama (Primary Sources)</span>
              <span class="badge bg-primary-subtle text-primary fs-11">{{ form.primary_sources.length }} Sumber</span>
            </div>
            <button type="button" @click="addPrimarySource" class="btn btn-xs btn-primary d-flex align-items-center gap-1 py-1 px-2 fs-12">
              <span class="material-symbols-outlined fs-14">add</span>
              <span>Tambah Sumber Primer</span>
            </button>
          </div>
          <div class="card-body p-3">
            <div v-if="form.primary_sources.length === 0" class="text-center py-3 text-muted fs-12 bg-light-subtle rounded border border-dashed">
              <span class="material-symbols-outlined fs-24 d-block mb-1 text-secondary">menu_book</span>
              Belum ada sumber primer yang dicatat. Klik <strong>"Tambah Sumber Primer"</strong> untuk mencatat sumber Turnitin utama.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered align-middle mb-0 fs-12">
                <thead class="table-light">
                  <tr>
                    <th style="width: 7%;" class="text-center"># Rank</th>
                    <th style="width: 38%;">Nama Sumber / Publikasi / URL <span class="text-danger">*</span></th>
                    <th style="width: 15%;" class="text-center">Kecocokan (%) <span class="text-danger">*</span></th>
                    <th style="width: 15%;">Status Penilaian</th>
                    <th style="width: 20%;">Catatan / Tindakan</th>
                    <th style="width: 5%;" class="text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(source, idx) in form.primary_sources" :key="idx">
                    <td class="text-center">
                      <input type="number" class="form-control form-control-sm text-center px-1" v-model.number="source.source_rank" min="1" />
                    </td>
                    <td>
                      <input type="text" class="form-control form-control-sm" v-model="source.source_name" placeholder="cth: repository.unair.ac.id / IEEE Xplore" required />
                    </td>
                    <td>
                      <div class="input-group input-group-sm">
                        <input type="number" step="0.1" class="form-control form-control-sm text-center" v-model.number="source.match_percentage" required />
                        <span class="input-group-text px-1">%</span>
                      </div>
                      <div v-if="source.match_percentage > form.applied_max_single_source" class="text-danger fs-11 mt-1">
                        &gt; Ambang ({{ form.applied_max_single_source }}%)
                      </div>
                    </td>
                    <td>
                      <select class="form-select form-select-sm" v-model="source.status">
                        <option value="Wajar">Wajar</option>
                        <option value="Perlu revisi">Perlu revisi</option>
                        <option value="Klarifikasi">Klarifikasi</option>
                      </select>
                    </td>
                    <td>
                      <input type="text" class="form-control form-control-sm" v-model="source.examiner_notes" placeholder="cth: Dikutip resmi / Parafrase" />
                    </td>
                    <td class="text-center">
                      <button type="button" @click="removePrimarySource(idx)" class="btn btn-sm btn-outline-danger p-1 border-0" title="Hapus Sumber">
                        <span class="material-symbols-outlined fs-16">delete</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- 6. HASIL PENGUKURAN SIMILARITAS -->
        <div class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex align-items-center justify-content-between py-2 px-3">
            <div class="d-flex align-items-center gap-2">
              <span class="material-symbols-outlined text-primary fs-18">analytics</span>
              <span class="fw-bold fs-13 text-dark">6. Hasil Pengukuran Similaritas</span>
            </div>
            <span class="badge bg-primary-subtle text-primary fs-11 d-inline-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-13">auto_awesome</span>
              <span>Auto-Kalkulasi Aktif (Dapat Diedit)</span>
            </span>
          </div>
          <div class="card-body p-3">
            <div class="row">
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12">Skor Awal (%) <span class="text-danger">*</span></label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.overall_before" required />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12 d-flex justify-content-between">
                  <span>Skor Akhir / Pasca-QC (%) <span class="text-danger">*</span></span>
                  <span class="text-muted fs-10">(Total Sumber)</span>
                </label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.overall_after" required />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12 d-flex justify-content-between">
                  <span>Kecocokan Tertinggi (%) <span class="text-danger">*</span></span>
                  <span class="text-muted fs-10">(Maks Sumber)</span>
                </label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.highest_source_match" required />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12">Nama Sumber Tertinggi</label>
                <input type="text" class="form-control form-control-sm" v-model="form.highest_source_name" placeholder="cth: repository.unair.ac.id" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Sumber Melampaui Batas (Count)</label>
                <input type="number" class="form-control form-control-sm" v-model="form.sources_above_threshold_count" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Status Pengujian <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.result_status" required>
                  <option value="Memenuhi">Memenuhi</option>
                  <option value="Perlu revisi">Perlu revisi</option>
                  <option value="Perlu klarifikasi">Perlu klarifikasi</option>
                </select>
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Rekomendasi <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.recommendation" required>
                  <option value="Layak diterbitkan">Layak diterbitkan</option>
                  <option value="Revisi naskah dan pemeriksaan ulang">Revisi naskah dan pemeriksaan ulang</option>
                  <option value="Klarifikasi sumber">Klarifikasi sumber</option>
                  <option value="Eskalasi ke QC">Eskalasi ke QC</option>
                </select>
              </div>
              <div class="col-12 mt-2">
                <label class="form-label fs-12">Catatan Rekomendasi</label>
                <textarea class="form-control form-control-sm" rows="2" v-model="form.recommendation_notes" placeholder="Catatan kesimpulan hasil pemeriksaan..."></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- 7. PEMERIKSAAN INTEGRITAS AKADEMIK & PENGESAHAN -->
        <div class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex align-items-center gap-2 py-2 px-3">
            <span class="material-symbols-outlined text-success fs-18">verified</span>
            <span class="fw-bold fs-13 text-dark">7. Pemeriksaan Integritas Akademik & Pengesahan</span>
          </div>
          <div class="card-body p-3">
            <div class="p-3 bg-light rounded-2 mb-3 border">
              <div class="fw-bold fs-13 mb-2 text-dark">Verifikasi 6 Parameter Integritas Akademik</div>
              <div class="row g-2 fs-12">
                <div class="col-md-4">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="form.integrity_plagiarism" id="intPlag">
                    <label class="form-check-label" for="intPlag">Bebas Plagiarisme Substantif</label>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="form.integrity_fabrication" id="intFab">
                    <label class="form-check-label" for="intFab">Bebas Fabrikasi Data</label>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="form.integrity_falsification" id="intFals">
                    <label class="form-check-label" for="intFals">Bebas Falsifikasi Hasil</label>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="form.integrity_duplicate" id="intDup">
                    <label class="form-check-label" for="intDup">Bebas Redundan / Duplikasi</label>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="form.integrity_authorship" id="intAuth">
                    <label class="form-check-label" for="intAuth">Validitas Kepengarangan</label>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="form.integrity_coi" id="intCoi">
                    <label class="form-check-label" for="intCoi">Bebas Konflik Kepentingan</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Verification Metadata (Masked for Public) -->
            <div class="row mb-3">
              <div class="col-md-4 mb-2">
                <label class="form-label fs-13 fw-semibold">Nama Penulis (Masked Publik)</label>
                <input type="text" class="form-control form-control-sm" v-model="form.author_name_masked" required placeholder="cth: A*** S***" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-13 fw-semibold">Judul Dokumen (Masked Publik)</label>
                <input type="text" class="form-control form-control-sm" v-model="form.document_title_masked" required placeholder="cth: Analisis Dampak..." />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-13 fw-semibold">Institusi / Afiliasi</label>
                <input type="text" class="form-control form-control-sm" v-model="form.institution" placeholder="Universitas ..." />
              </div>
            </div>

            <!-- Examiner & Signatory Details -->
            <div class="card bg-light-subtle border p-3 rounded-3 mb-3">
              <h6 class="fs-13 fw-bold text-dark mb-2 d-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-18 text-primary">draw</span>
                <span>Pemeriksa & Penandatangan Pengesahan Protokol</span>
              </h6>
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Nama Pemeriksa (Examiner) <span class="text-danger">*</span></label>
                  <input type="text" class="form-control form-control-sm" v-model="form.examiner_name" required />
                </div>
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Pilih Penandatangan Terdaftar</label>
                  <select class="form-select form-select-sm" v-model="form.signatory_id" @change="onSignatoryChange">
                    <option :value="null">-- Gunakan Penandatangan Default --</option>
                    <option v-for="sig in signatories" :key="sig.id" :value="sig.id">
                      {{ sig.name }} ({{ sig.position }}) {{ sig.is_default ? '★ Default' : '' }}
                    </option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Nama & Jabatan Pengesahan</label>
                  <input type="text" class="form-control form-control-sm mb-1" v-model="form.signatory_name" placeholder="Nama penandatangan" />
                  <input type="text" class="form-control form-control-sm" v-model="form.signatory_position" placeholder="Jabatan penandatangan" />
                </div>
                <div v-if="selectedSignatoryPreviewUrl" class="col-12 mt-1">
                  <div class="d-flex align-items-center gap-3 p-2 bg-white rounded border">
                    <span class="fs-11 text-muted fw-semibold">Preview Tanda Tangan:</span>
                    <img :src="selectedSignatoryPreviewUrl" alt="Tanda Tangan" style="height: 32px; max-width: 120px; object-fit: contain;" />
                    <span class="fs-11 text-success d-flex align-items-center gap-1">
                      <span class="material-symbols-outlined fs-14">check_circle</span>
                      <span>Siap disematkan pada sertifikat protokol</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
              <button type="submit" class="btn btn-primary px-4 py-2 d-flex align-items-center gap-2" :disabled="submitting">
                <span v-if="submitting" class="spinner-border spinner-border-sm"></span>
                <span class="material-symbols-outlined fs-18">save</span>
                <span>{{ protocol ? 'Perbarui Protokol' : 'Simpan & Terbitkan Protokol RC-SIM' }}</span>
              </button>
            </div>
          </div>
        </div>

      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted, computed, watch } from 'vue';
import api from '../../../services/api';
import protocolSignatoryService from '../../../services/protocolSignatoryService';

export default defineComponent({
  name: 'SimilarityProtocolForm',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const protocol = computed(() => props.order?.protocol_similarity || null);
    const profiles = ref<any[]>([]);
    const signatories = ref<any[]>([]);
    const submitting = ref(false);
    const generatingPdf = ref(false);

    const orderRequirement = computed(() => {
      const d = props.order?.plagiarism_detail || props.order?.all_detail;
      if (!d) return null;
      return {
        excludeQuotes: Boolean(d.exclude_quotes),
        excludeBiblio: Boolean(d.exclude_bibliography),
        excludeMatch: Boolean(d.exclude_match)
      };
    });

    const form = reactive({
      profile_id: (props.order?.similarity_profile_id || props.order?.similarityProfile?.id || null) as number | null,
      service_classification: (props.order?.service_classification || 'Artikel Jurnal') as string,
      manuscript_version: 'Draft',
      page_count: props.order?.plagiarism_detail?.page_count || 10,
      word_count: 3500,
      tool_name: 'Turnitin',
      examiner_account: 'RBN Official',
      repository_setting: 'No repository',
      report_submission_id: 'TRN-' + Date.now(),
      report_date: new Date().toISOString().substring(0, 10),
      file_hash_sha256: Array.from({length: 64}, () => Math.floor(Math.random()*16).toString(16)).join(''),
      applied_max_overall: 20,
      applied_max_single_source: 5,
      applied_exclude_biblio: props.order?.plagiarism_detail?.exclude_bibliography !== undefined
        ? Boolean(props.order.plagiarism_detail.exclude_bibliography)
        : (props.order?.all_detail?.exclude_bibliography !== undefined
            ? Boolean(props.order.all_detail.exclude_bibliography)
            : true),
      applied_exclude_quotes: props.order?.plagiarism_detail?.exclude_quotes !== undefined
        ? Boolean(props.order.plagiarism_detail.exclude_quotes)
        : (props.order?.all_detail?.exclude_quotes !== undefined
            ? Boolean(props.order.all_detail.exclude_quotes)
            : true),
      small_matches_mode: (props.order?.plagiarism_detail?.exclude_match || props.order?.all_detail?.exclude_match)
        ? 'words'
        : 'none',
      small_matches_value: (props.order?.plagiarism_detail?.exclude_match || props.order?.all_detail?.exclude_match)
        ? 8
        : null as number | null,
      custom_institution_note: '',
      overall_before: 25,
      overall_after: 4.5,
      highest_source_match: 4.5,
      highest_source_name: 'Repository Publikasi Nasional',
      sources_above_threshold_count: 0,
      result_status: 'Memenuhi',
      recommendation: 'Layak diterbitkan',
      recommendation_notes: 'Naskah telah memenuhi batas standar integritas akademik PT RBN.',
      integrity_plagiarism: true,
      integrity_fabrication: true,
      integrity_falsification: true,
      integrity_duplicate: true,
      integrity_authorship: true,
      integrity_coi: true,
      integrity_notes: '',
      examiner_name: 'Staff Analis RBN',
      qc_name: 'Tim QC RBN',
      service_manager_name: 'Manajer Layanan RBN',
      director_name: 'Direktur RBN',
      signatory_id: null as number | null,
      signatory_name: '',
      signatory_position: '',
      signatory_image: null as string | null,
      author_name_masked: maskName(props.order?.customer?.name || 'Customer RBN'),
      document_title_masked: 'Naskah Riset ' + (props.order?.order_code || ''),
      institution: 'Institusi Terdaftar',
      primary_sources: [
        { source_rank: 1, source_name: 'Repository Publikasi Nasional', match_percentage: 4.5, status: 'Wajar', examiner_notes: 'Dikutip resmi' }
      ] as Array<{ source_rank: number; source_name: string; match_percentage: number; status: string; examiner_notes: string }>,
      manual_exclusions: [] as Array<{ source_title_url: string; source_type: string; match_before: number; exclusion_reason: string; authorized_by: string; excluded_at?: string }>
    });

    function maskName(str: string): string {
      if (!str || str.length <= 2) return str;
      const parts = str.split(' ');
      return parts.map(p => p[0] + '***' + (p.length > 3 ? p[p.length - 1] : '')).join(' ');
    }

    const loadProfiles = async () => {
      try {
        const res = await api.get('/similarity-profiles');
        profiles.value = res.data.data || [];
        if (!protocol.value && form.profile_id) {
          onProfileChange();
        }
      } catch (err) {
        console.error('Failed to fetch similarity profiles:', err);
      }
    };

    const loadSignatories = async () => {
      try {
        const res = await protocolSignatoryService.getActive();
        signatories.value = res.data.data || [];
        if (!form.signatory_id && !protocol.value?.signatory_id) {
          const def = signatories.value.find((s: any) => s.is_default) || signatories.value[0];
          if (def) {
            form.signatory_id = def.id;
            form.signatory_name = def.name;
            form.signatory_position = def.position;
            form.signatory_image = def.signature_image;
            form.director_name = def.name;
          }
        }
      } catch (err) {
        console.error('Failed to load active signatories:', err);
      }
    };

    const onSignatoryChange = () => {
      const chosen = signatories.value.find((s: any) => s.id === form.signatory_id);
      if (chosen) {
        form.signatory_name = chosen.name;
        form.signatory_position = chosen.position;
        form.signatory_image = chosen.signature_image;
        form.director_name = chosen.name;
      }
    };

    const getFileUrl = (path?: string | null) => {
      if (!path) return '';
      if (path.startsWith('blob:') || path.startsWith('data:')) return path;
      if (path.includes('/storage/')) {
        const cleanPath = path.replace(/^https?:\/\/[^/]+\/storage\//, '').replace(/^storage\//, '').replace(/^\//, '');
        const baseUrl = process.env.NODE_ENV === 'production'
          ? 'https://backend-beta.rayacheck.id'
          : 'http://jurnal-backend.test';
        return `${baseUrl}/storage/${cleanPath}`;
      }
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      const cleanPath = path.replace(/^\//, '');
      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://backend-beta.rayacheck.id'
        : 'http://jurnal-backend.test';
      return `${baseUrl}/storage/${cleanPath}`;
    };

    const selectedSignatoryPreviewUrl = computed(() => {
      let raw = '';
      if (form.signatory_id) {
        const chosen = signatories.value.find((s: any) => s.id === form.signatory_id);
        if (chosen) raw = chosen.signature_image_url || chosen.signature_image || '';
      } else if (protocol.value?.signatory_image_url || protocol.value?.signatory_image) {
        raw = protocol.value.signatory_image_url || protocol.value.signatory_image || '';
      } else {
        const def = signatories.value.find((s: any) => s.is_default);
        raw = def?.signature_image_url || def?.signature_image || '';
      }
      return raw ? getFileUrl(raw) : null;
    });

    const isSyncing = ref(false);

    const evaluateStatusAndRecommendation = () => {
      const maxOverall = Number(form.applied_max_overall) || 20;
      const maxSingle = Number(form.applied_max_single_source) || 5;
      const overall = Number(form.overall_after) || 0;
      const highest = Number(form.highest_source_match) || 0;

      const isPassed = overall <= maxOverall && highest <= maxSingle;

      if (isPassed) {
        form.result_status = 'Memenuhi';
        form.recommendation = 'Layak diterbitkan';
        form.recommendation_notes = 'Naskah telah memenuhi batas standar integritas akademik PT RBN.';
      } else {
        form.result_status = 'Perlu revisi';
        form.recommendation = 'Revisi naskah dan pemeriksaan ulang';
        form.recommendation_notes = `Naskah melampaui batas ambang similaritas (Total: ${overall}% / Maks: ${maxOverall}%, Sumber Tertinggi: ${highest}% / Maks: ${maxSingle}%). Disarankan parafrase naskah.`;
      }
    };

    const recalculateFromPrimarySources = () => {
      if (isSyncing.value) return;

      const sources = form.primary_sources || [];
      if (sources.length === 0) {
        form.overall_after = 0;
        form.highest_source_match = 0;
        form.highest_source_name = '';
        form.sources_above_threshold_count = 0;
        evaluateStatusAndRecommendation();
        return;
      }

      // 1. Hitung total persentase sumber primer (Skor Akhir / Pasca-QC (%))
      const totalMatch = sources.reduce((sum, s) => {
        const val = Number(s.match_percentage);
        return sum + (isNaN(val) ? 0 : val);
      }, 0);

      // 2. Cari persentase kecocokan tertinggi dan sumber terkait
      let maxMatch = 0;
      let topSourceName = '';
      for (const s of sources) {
        const val = Number(s.match_percentage);
        if (!isNaN(val) && val > maxMatch) {
          maxMatch = val;
          topSourceName = s.source_name || '';
        }
      }

      form.overall_after = Number(totalMatch.toFixed(1));
      form.highest_source_match = Number(maxMatch.toFixed(1));
      if (topSourceName) {
        form.highest_source_name = topSourceName;
      }

      // 3. Hitung jumlah sumber melampaui batas single source
      const maxSingle = Number(form.applied_max_single_source) || 5;
      form.sources_above_threshold_count = sources.filter(s => {
        const val = Number(s.match_percentage);
        return !isNaN(val) && val > maxSingle;
      }).length;

      // 4. Update status pengujian & rekomendasi otomatis
      evaluateStatusAndRecommendation();
    };

    const onThresholdChange = () => {
      if (!isSyncing.value) {
        const maxSingle = Number(form.applied_max_single_source) || 5;
        form.sources_above_threshold_count = (form.primary_sources || []).filter(s => {
          const val = Number(s.match_percentage);
          return !isNaN(val) && val > maxSingle;
        }).length;
        evaluateStatusAndRecommendation();
      }
    };

    const showDetailedOverview = ref(true);

    const formatSmallMatches = (proto: any) => {
      if (!proto) return 'Tidak / None';
      const mode = proto.small_matches_mode || proto.profile?.small_matches_mode || 'none';
      const val = proto.small_matches_value ?? proto.profile?.small_matches_value ?? null;
      if (mode === 'words') return (val || 8) + ' kata';
      if (mode === 'percentage') return (val || 1) + '%';
      return 'Tidak / None';
    };

    const syncFromOrderRequirements = (force = false) => {
      if (!orderRequirement.value) return;
      if (force || !protocol.value) {
        if (orderRequirement.value.excludeQuotes !== undefined) {
          form.applied_exclude_quotes = orderRequirement.value.excludeQuotes;
        }
        if (orderRequirement.value.excludeBiblio !== undefined) {
          form.applied_exclude_biblio = orderRequirement.value.excludeBiblio;
        }
        if (orderRequirement.value.excludeMatch) {
          if (form.small_matches_mode === 'none') {
            form.small_matches_mode = 'words';
            form.small_matches_value = 8;
          }
        } else {
          form.small_matches_mode = 'none';
          form.small_matches_value = null;
        }
      }
    };

    const onProfileChange = () => {
      if (!form.profile_id) return;
      const matched = profiles.value.find(p => p.id === form.profile_id);
      if (matched) {
        if (matched.service_classification) {
          form.service_classification = matched.service_classification;
        }
        form.applied_max_overall = Number(matched.default_max_overall) || 20;
        form.applied_max_single_source = Number(matched.default_max_single_source) || 5;

        if (orderRequirement.value) {
          // Sinkronkan filter dengan pesanan pelanggan
          form.applied_exclude_biblio = orderRequirement.value.excludeBiblio;
          form.applied_exclude_quotes = orderRequirement.value.excludeQuotes;
          if (orderRequirement.value.excludeMatch) {
            form.small_matches_mode = (matched.small_matches_mode && matched.small_matches_mode !== 'none')
              ? matched.small_matches_mode
              : 'words';
            form.small_matches_value = matched.small_matches_value || (form.small_matches_mode === 'words' ? 8 : 1);
          } else {
            form.small_matches_mode = 'none';
            form.small_matches_value = null;
          }
        } else {
          form.applied_exclude_biblio = Boolean(matched.exclude_bibliography);
          form.applied_exclude_quotes = Boolean(matched.exclude_quotes);
          form.small_matches_mode = matched.small_matches_mode || 'none';
          form.small_matches_value = matched.small_matches_value || null;
        }

        form.institution = matched.profile_name || 'Institusi';
        onThresholdChange();
      }
    };

    const addPrimarySource = () => {
      form.primary_sources.push({
        source_rank: form.primary_sources.length + 1,
        source_name: '',
        match_percentage: 1.0,
        status: 'Wajar',
        examiner_notes: ''
      });
      recalculateFromPrimarySources();
    };

    const removePrimarySource = (index: number) => {
      form.primary_sources.splice(index, 1);
      form.primary_sources.forEach((s, idx) => {
        s.source_rank = idx + 1;
      });
      recalculateFromPrimarySources();
    };

    const addManualExclusion = () => {
      form.manual_exclusions.push({
        source_title_url: '',
        source_type: 'Self-match',
        match_before: 5.0,
        exclusion_reason: 'Self-citation karya atau repositori penulis sendiri yang diverifikasi',
        authorized_by: 'Pemeriksa',
        excluded_at: new Date().toISOString().substring(0, 19).replace('T', ' ')
      });
    };

    const removeManualExclusion = (index: number) => {
      form.manual_exclusions.splice(index, 1);
    };

    const syncFormWithProtocol = (proto: any) => {
      if (!proto) return;
      isSyncing.value = true;
      try {
        Object.assign(form, proto);

        if (proto.small_matches_mode) {
          form.small_matches_mode = proto.small_matches_mode;
          form.small_matches_value = proto.small_matches_value !== null && proto.small_matches_value !== undefined
            ? Number(proto.small_matches_value)
            : null;
        } else if (proto.profile?.small_matches_mode) {
          form.small_matches_mode = proto.profile.small_matches_mode;
          form.small_matches_value = proto.profile.small_matches_value !== null && proto.profile.small_matches_value !== undefined
            ? Number(proto.profile.small_matches_value)
            : null;
        } else if (orderRequirement.value?.excludeMatch) {
          form.small_matches_mode = 'words';
          form.small_matches_value = 8;
        } else {
          form.small_matches_mode = 'none';
          form.small_matches_value = null;
        }

        const sources = proto.primary_sources || proto.primarySources;
        if (Array.isArray(sources) && sources.length > 0) {
          form.primary_sources = JSON.parse(JSON.stringify(sources));
        }

        const exclusions = proto.manual_exclusions || proto.manualExclusions;
        if (Array.isArray(exclusions)) {
          form.manual_exclusions = JSON.parse(JSON.stringify(exclusions));
        }
      } finally {
        setTimeout(() => {
          isSyncing.value = false;
        }, 50);
      }
    };

    const saveProtocol = async () => {
      submitting.value = true;
      try {
        if (protocol.value) {
          await api.put(`/protocol-similarities/${protocol.value.id}`, form);
          alert('Protokol berhasil diperbarui.');
        } else {
          await api.post(`/orders/${props.order.order_id}/protocol-similarity`, form);
          alert('Protokol RC-SIM berhasil diterbitkan!');
        }
        emit('refresh');
      } catch (err: any) {
        console.error('Error saving similarity protocol:', err);
        alert(err.response?.data?.message || 'Gagal menyimpan protokol.');
      } finally {
        submitting.value = false;
      }
    };

    const generatePdf = async () => {
      generatingPdf.value = true;
      try {
        const res = await api.post(`/orders/${props.order.order_id}/generate-protocol-pdf`);
        alert('PDF Protokol resmi berhasil digenerate.');
        emit('refresh');
      } catch (err: any) {
        console.error('Error generating PDF:', err);
        alert(err.response?.data?.message || 'Gagal generate PDF.');
      } finally {
        generatingPdf.value = false;
      }
    };

    const getVerificationUrl = (code: string) => {
      const baseUrl = window.location.origin;
      return `${baseUrl}/verify/${code}`;
    };

    onMounted(async () => {
      await loadProfiles();
      await loadSignatories();
      if (protocol.value) {
        syncFormWithProtocol(protocol.value);
      } else {
        syncFromOrderRequirements(false);
        if (props.order?.similarity_profile_id) {
          form.profile_id = props.order.similarity_profile_id;
          form.service_classification = props.order.service_classification || form.service_classification;
          onProfileChange();
        }
        recalculateFromPrimarySources();
      }
    });

    watch(protocol, (newVal) => {
      if (newVal) {
        syncFormWithProtocol(newVal);
      }
    }, { deep: true });

    watch(
      () => form.primary_sources,
      () => {
        if (isSyncing.value) return;
        recalculateFromPrimarySources();
      },
      { deep: true }
    );

    watch(() => props.order, (newOrder) => {
      if (!protocol.value && newOrder) {
        syncFromOrderRequirements(false);
        if (newOrder.similarity_profile_id) {
          form.profile_id = newOrder.similarity_profile_id;
          form.service_classification = newOrder.service_classification || form.service_classification;
          onProfileChange();
        } else if (newOrder.service_classification) {
          form.service_classification = newOrder.service_classification;
        }
      }
    }, { deep: true });

    return {
      protocol,
      profiles,
      signatories,
      onSignatoryChange,
      selectedSignatoryPreviewUrl,
      form,
      orderRequirement,
      syncFromOrderRequirements,
      submitting,
      generatingPdf,
      showDetailedOverview,
      formatSmallMatches,
      onProfileChange,
      onThresholdChange,
      recalculateFromPrimarySources,
      evaluateStatusAndRecommendation,
      addPrimarySource,
      removePrimarySource,
      addManualExclusion,
      removeManualExclusion,
      saveProtocol,
      generatePdf,
      getFileUrl,
      getVerificationUrl
    };
  }
});
</script>
